import PropTypes from 'prop-types';

/**
 * Supported keys controlling action cell behavior.
 */
export const ACTION_TYPES = {
	CHECKBOX: 'checkbox',
	EDIT: 'edit',
	REMOVE: 'remove',
	TOGGLE: 'toggle',
	MODIFY_TOGGLE: 'modify_toggle',
	REVOKE_TOGGLE: 'revoke_toggle',
	REACTIVATE_TOGGLE: 'reactivate_toggle',
	FLAG_BUSINESS: 'business_restriction',
	FLAG_LOCK: 'lock',
	FLAG_PERSON: 'person_restriction',
	FLAG_PROBLEM: 'problem',
	FLAG_TIME: 'time_sensitive',
	FLAG_UPDATE: 'update',
	REVERIFY: 'reverify',
	DONE: 'done',
	CANCEL: 'cancel',
	SEND: 'send',
};

/**
 * Supported action cell icon types.
 */
export const ACTION_ICON_TYPES = {
	ADD: 'add',
	BUSINESS: 'business_center',
	DELETE: 'delete',
	CANCEL: 'undo',
	DONE: 'done',
	EDIT: 'edit',
	LOCK: 'lock',
	PERSON: 'person',
	PROBLEM: 'report_problem',
	REMOVE: 'remove',
	REVERIFY: 'library_add_check',
	SCHEDULE: 'schedule',
	REMIND: 'notifications',
	UPDATE: 'update',
};

/**
 * Base `prop-types` handler function validator.
 * Ensures property is provided and of expected type when parent property provided.
 * @param {Object} props all JSX attributes passed to component.
 * @param {string} propName key of target property.
 * @param {string} componentName name of component.
 * @param {string} parentPropName name of property requiring target property.
 * @returns {Error|undefined} raised error message if validation fails.
 */
const validateHandler = (props, propName, componentName, parentPropName) => {
	if (props.hasOwnProperty(parentPropName)) {
		if (!props.hasOwnProperty(propName)) {
			return new Error(
				'The prop `' +
					propName +
					'` is marked as required in `' +
					componentName +
					'` when prop `' +
					parentPropName +
					'` is provided, but its value is `undefined`.'
			);
		} else {
			const type = typeof props[propName];
			if (!(type === 'function')) {
				return new Error(
					'Invalid prop `' +
						propName +
						'` of type `' +
						type +
						'` supplied to `' +
						componentName +
						'`, expected `function`.'
				);
			}
		}
	}
	return;
};

export const dataTablePropTypes = {
	actions: PropTypes.arrayOf(
		PropTypes.shape({
			Type: PropTypes.oneOf(
				Object.keys(ACTION_TYPES).map(key => ACTION_TYPES[key])
			),
			onChangedHandler: function (props, propName, componentName) {
				return validateHandler(props, propName, componentName, 'Type');
			},
			disabled: PropTypes.bool,
			Header: PropTypes.shape({
				Checked: PropTypes.bool.isRequired,
				Key: PropTypes.string.isRequired,
				onChangedHandler: PropTypes.func,
				disabled: PropTypes.bool,
			}),
		})
	),
	areRowsLazyLoaded: PropTypes.bool,
	className: PropTypes.string,
	errorMessage: PropTypes.string,
	filtered: PropTypes.bool,
	filteredOptions: PropTypes.arrayOf(PropTypes.string),
	headers: PropTypes.arrayOf(
		PropTypes.shape({
			props: PropTypes.shape({
				alignEnd: PropTypes.bool,
				alignMiddle: PropTypes.bool,
				alignStart: PropTypes.bool,
				children: PropTypes.node,
				hasFormControl: PropTypes.bool,
				isNumeric: PropTypes.bool,
				onSortChange: PropTypes.func,
				sort: PropTypes.oneOf([null, 1, -1]),
			}),
			Value: PropTypes.string,
		})
	).isRequired,
	iconLegend: PropTypes.arrayOf(
		PropTypes.shape({
			BindTo: PropTypes.number.isRequired,
			ValuePrimary: PropTypes.string.isRequired,
			ValueSecondary: PropTypes.string,
		})
	),
	indexed: PropTypes.bool,
	indexedHelperText: PropTypes.string,
	resetSearchValue: PropTypes.string,
	infiniteScroll: PropTypes.bool,
	infiniteScrollThreshold: PropTypes.number,
	isReadOnly: PropTypes.bool,
	label: PropTypes.string,
	searchfieldText: PropTypes.string,
	handleRightClick: PropTypes.func,
	onChipSetChangedHandler: function (props, propName, componentName) {
		return validateHandler(props, propName, componentName, 'filtered');
	},
	onLoadMoreResultsHandler: PropTypes.func,
	onRowClickedHandler: PropTypes.func,
	onTextFieldChangedHandler: function (props, propName, componentName) {
		return validateHandler(props, propName, componentName, 'indexed');
	},
	pageSize: PropTypes.number,
	rows: PropTypes.arrayOf(
		PropTypes.shape({
			ActionCells: PropTypes.arrayOf(
				PropTypes.shape({
					BindTo: PropTypes.number.isRequired,
					Checked: PropTypes.bool.isRequired,
					Key: PropTypes.string.isRequired,
					Text: PropTypes.string,
				})
			),
			Cells: PropTypes.objectOf(
				PropTypes.shape({
					Value: PropTypes.oneOfType([
						PropTypes.string,
						PropTypes.element,
					]),
					Validation: PropTypes.shape({
						Key: PropTypes.string.isRequired,
						memberToValidateKey: PropTypes.string,
						Value: PropTypes.shape({
							ErrorMessage: PropTypes.string,
						}),
					}),
					props: PropTypes.shape({
						alignEnd: PropTypes.bool,
						alignMiddle: PropTypes.bool,
						alignStart: PropTypes.bool,
						children: PropTypes.node,
						isNumeric: PropTypes.bool,
						cellTextOnClick: PropTypes.func,
						extendtext: PropTypes.number,
						breakText: PropTypes.bool,
						leadingIcon: PropTypes.element,
					}),
					isEditable: PropTypes.bool,
				})
			),
			Key: PropTypes.string.isRequired,
			props: PropTypes.shape({
				activated: PropTypes.bool,
				selected: PropTypes.bool,
			}),
		})
	).isRequired,
	stickyColumns: PropTypes.number,
	stickyRows: PropTypes.number,
	sublabel: PropTypes.string,
	validation: PropTypes.shape({
		minimumThreshold: PropTypes.number,
		onCellChangedHandler: PropTypes.func.isRequired,
	}),
};
